<script setup lang="ts">
import GwDragDropper from '@/components/lib/GwDragDropper/GwDragDropper.vue'
import { type DragDropArgs } from '@/components/lib/GwDragDropper/GwDragDropperUtils'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import { t, te } from '@/locales/i18n'
import type { HandHistoryReportColumn } from '@/types/api/reports'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwScrollCont from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'

export interface AnalyzerReportTableColumnManagementProps {
  columns: HandHistoryReportColumn[]
}

export type ColumnManagementEmits = {
  'drag:drop:column': [Event, DragDropArgs<HandHistoryReportColumn>]
  'toggle:column': [HandHistoryReportColumn]
}

withDefaults(defineProps<AnalyzerReportTableColumnManagementProps>(), {})

const emit = defineEmits<ColumnManagementEmits>()

const handleDrop = (e: Event, args: DragDropArgs<HandHistoryReportColumn>) => {
  emit('drag:drop:column', e, args)
}

const handleVisibleUpdate = (item: HandHistoryReportColumn) => {
  emit('toggle:column', item)
}

const initDrag = (e: Event, item: HandHistoryReportColumn, methods: any) => {
  methods.startDrag(e, item)
}
</script>
<template>
  <GwDragDropper
    dropTargetIdPart="reportColumn_"
    direction="vertical"
    v-bind="$attrs"
    @drag:drop="handleDrop"
  >
    <template #shallowitem="{ draggedData: item }">
      <GwBox
        v-if="item"
        variant="box_dragDrop_item"
        style="padding: 0 var(--app-pad-3)"
      >
        <span v-html="te(`hhv3.table_head.${item.column.field_name}`, item.column.field_name)" />
      </GwBox>
    </template>
    <template #default="{ methods: dragDropperMethods, isDragging }">
      <div
        class="f-column flex-1 mh-0 mw-0 w-100"
        style="
          gap: var(--app-gap-3);
          padding: var(--app-pad-3);
          min-width: clamp(1px, 24rem, 100vw);
        "
      >
        <GwText variant="text_grey_medium_400_uppercase">
          {{ t(`hhv3.manage_columns`) }}
        </GwText>
        <GwScrollCont
          data-tst="column_management"
          direction="vertical"
          class="w-100 flex-1 mh-0 mw-0"
          stretched
          hasResizeObserver
          hasOverflowSurfaces
          :hasEdgeAutoScroll="isDragging"
        >
          <div
            class="mh-0 mw-0 no-shrink f-column flex-1"
            style="gap: var(--app-gap-4)"
          >
            <div
              class="f-column w-100"
              style="gap: var(--app-gap-2)"
            >
              <GwBox
                v-for="item in columns || []"
                :id="`reportColumn_${item.column.id}`"
                :key="item.column.id"
                class="w-100"
                variant="box_grey_white_big"
                :disabled="item.is_disabled"
                :data-tst="`column_${item.column.field_name}`"
              >
                <GwTooltipNew
                  :closeOnContentHover="true"
                  placement="bottom"
                  :tooltip="t('hhv3.tooltips.drag')"
                >
                  <template #opener="{ listeners }">
                    <GwBtn
                      icon="mdi-drag"
                      variant="btn_text_grey_primary"
                      @mousedown="initDrag($event, item, dragDropperMethods)"
                      @touchstart="initDrag($event, item, dragDropperMethods)"
                      v-on="listeners"
                    />
                  </template>
                </GwTooltipNew>
                <span
                  v-html="te(`hhv3.table_head.${item.column.field_name}`, item.column.field_name)"
                />
                <GwTooltipNew
                  :closeOnContentHover="true"
                  placement="left"
                  :tooltip="t(`hhv3.tooltips.${item.is_hidden ? 'show' : 'hide'}`)"
                >
                  <template #opener="{ listeners }">
                    <GwBtn
                      class="ml-auto"
                      :variant="item.is_hidden ? 'btn_primaryV2' : 'btn_error'"
                      :icon="item.is_hidden ? 'mdi-plus' : 'mdi-minus'"
                      :data-tst="`btn_column_control`"
                      :disabled="item.is_disabled"
                      @click="handleVisibleUpdate(item)"
                      @mousedown.stop
                      @touchstart.stop
                      v-on="listeners"
                    />
                  </template>
                </GwTooltipNew>
              </GwBox>
            </div>
          </div>
        </GwScrollCont>
      </div>
    </template>
  </GwDragDropper>
</template>
